import React, { useState, useEffect, useRef, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultValues, FieldPath, useFieldArray, useForm } from "react-hook-form";
import FormFieldInput from "@/src/components/FormField/FormFieldInput";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import CheckboxGroup from "@/src/components/CheckboxGroup/CheckboxGroup";
import {
  AttachmentType,
  CreateApplicationFormValuesType,
  STAKEHOLDER_TYPE,
  StakholderType,
  YES_NO_OPTIONS,
  YesNoOptions,
  createApplicationFormSchema,
} from "../schemas";
import FormSection from "@/src/components/FormSection/FormSection";
import FormFieldTextarea from "../../FormField/FormFieldTextarea";
import FormFieldSelect from "../../FormField/FormFieldSelect";
import { Trash2, Plus, Send } from "lucide-react";
import SearchLocation from "../../SearchLocation/SearchLocation";
import { Parcel } from "../../ParcelPreview/ParcelPreview";
import { ProcedurePayload } from "@/src/api/Tenant";
import useUldk from "@/src/hooks/useUldk";
import { Checkbox } from "@/components/ui/checkbox";
import { PLAN_OGOLNY_SYMBOLS, MPZP_SYMBOLS } from "@/src/lib/symbolColors";
import { getProcedureMapView } from "@/src/lib/procedureMapView";
import { formatPostalCode } from "@/src/lib/postalCode";

const EMPTY_MAILING_ADDRESS = {
  country: null,
  voivodeship: null,
  district: null,
  tenant: null,
  street: null,
  houseNumber: null,
  apartmentNumber: undefined,
  town: null,
  postalCode: null,
};

const EMPTY_REPRESENTATIVE = {
  ...EMPTY_MAILING_ADDRESS,
  name: null,
  type: null,
  email: "",
  phone: null,
  epuapAdress: null,
};

/**
 * Wartości startowe formularza — jedno źródło dla useForm i dla resetu po
 * odrzuceniu przywróconego szkicu. Kolejność nadpisań: szkic, potem
 * defaultValues z propsów.
 */
function buildInitialValues(
  loadedDraft?: Partial<CreateApplicationFormValuesType>,
  defaultValues?: Partial<CreateApplicationFormValuesType>,
): DefaultValues<CreateApplicationFormValuesType> {
  return {
    applicants: { country: "Polska" },
    mailingAddress: null,
    representative: null,
    attachmentTypes: [],
    files: undefined,
    ...loadedDraft,
    ...defaultValues,
  };
}

const PARCEL_CONTENT_HELP =
  "Napisz konkretnie, czego oczekujesz dla tej działki, np. zmiany jej przeznaczenia, dopuszczenia zabudowy albo zmiany parametrów zabudowy.";

const DESTINATION_SYMBOL_HELP_POG = "Wskaż oczekiwaną strefę planistyczną planu ogólnego.";
const DESTINATION_SYMBOL_HELP_MPZP =
  "Wskaż oczekiwany symbol przeznaczenia terenu według legendy projektu miejscowego planu zagospodarowania przestrzennego.";
const DESTINATION_SYMBOL_HELP_ANY =
  "Wskaż oczekiwaną strefę planistyczną dla planu ogólnego albo oczekiwany symbol przeznaczenia terenu dla MPZP.";

type CreateApplicationFormPropsType = {
  defaultValues?: Partial<CreateApplicationFormValuesType>;
  onSubmit: (values: CreateApplicationFormValuesType) => void;
  applicantsParcels?: Parcel[];
  procedurePayload: ProcedurePayload;
  preSelectedParcels?: Array<{ id: string; geometry: any }>;
  draftStorageKey?: string;
  onSubmitSuccess?: () => void;
};

function CreateApplicationForm({
  onSubmit,
  defaultValues,
  applicantsParcels,
  procedurePayload,
  preSelectedParcels,
  draftStorageKey,
  onSubmitSuccess,
}: CreateApplicationFormPropsType) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isPlanOgolny = procedurePayload?.spatialPlanningActType?.includes("3.1");
  const isMPZP = procedurePayload?.spatialPlanningActType?.includes("3.2");
  const isComment = String(procedurePayload?.typeOfLetter || "").includes("2.2");
  const letterGen = isComment ? "uwagi" : "wniosku";
  const planSymbols = procedurePayload?.mpzpSymbols;
  const symbolOptions =
    isMPZP && planSymbols && planSymbols.length > 0
      ? planSymbols.map((s) => ({ value: s.symbol, label: `${s.symbol} – ${s.nazwa}` }))
      : isPlanOgolny
        ? PLAN_OGOLNY_SYMBOLS
        : isMPZP
          ? MPZP_SYMBOLS
          : null;
  const DESTINATION_SYMBOL_HELP = isPlanOgolny
    ? DESTINATION_SYMBOL_HELP_POG
    : isMPZP
      ? DESTINATION_SYMBOL_HELP_MPZP
      : DESTINATION_SYMBOL_HELP_ANY;
  const contentPlaceholder = isComment
    ? isMPZP
      ? "np. Wnoszę uwagę do projektu planu: proszę o przeznaczenie działki nr ... pod zabudowę mieszkaniową jednorodzinną (MN) zamiast ..."
      : "np. Wnoszę uwagę do projektu aktu dotyczącą działki nr ..."
    : "np. Wnioskuję o przeznaczenie działki nr ... na cele zabudowy mieszkaniowej jednorodzinnej...";
  const {
    center: procedureMapCenter,
    zoom: procedureMapZoom,
  } = getProcedureMapView(procedurePayload);

  const { voivodeships, districts, fetchDistricts, fetchTenants, tenants } = useUldk({});

  const [loadedDraft] = useState<Partial<CreateApplicationFormValuesType> | undefined>(() => {
    if (!draftStorageKey) return undefined;
    try {
      const stored = localStorage.getItem(draftStorageKey);
      if (stored) return JSON.parse(stored) as Partial<CreateApplicationFormValuesType>;
    } catch { /* ignore */ }
    return undefined;
  });

  // Stan sekcji 5/6 i banera szkicu wynika z wartości startowych — bez
  // efektu po mount (i bez dodatkowego renderu, po którym sekcje "doskakiwały").
  const [initialValues] = useState(() => buildInitialValues(loadedDraft, defaultValues));
  const [showMailingAddress, setShowMailingAddress] = useState(!!initialValues.mailingAddress);
  const [showRepresentative, setShowRepresentative] = useState(!!initialValues.representative);
  const [draftRecovered, setDraftRecovered] = useState(!!loadedDraft);

  const form = useForm<CreateApplicationFormValuesType>({
    resolver: zodResolver(createApplicationFormSchema),
    defaultValues: initialValues,
  });

  // Auto-save draft to localStorage with debounce
  useEffect(() => {
    if (!draftStorageKey) return;

    const subscription = form.watch((values) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        if (!form.formState.isDirty) return;
        try {
          // Strip the files field (FileList is not serializable)
          const { files, ...saveable } = values;
          localStorage.setItem(draftStorageKey, JSON.stringify(saveable));
        } catch {
          /* quota exceeded or other localStorage error */
        }
      }, 1500);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStorageKey]);

  const clearDraft = useCallback(() => {
    if (!draftStorageKey) return;
    try {
      localStorage.removeItem(draftStorageKey);
    } catch {
      /* ignore */
    }
    setDraftRecovered(false);
  }, [draftStorageKey]);

  const handleSubmit = (values: CreateApplicationFormValuesType) => {
    onSubmit(values);
    clearDraft();
    onSubmitSuccess?.();
  };

  const {
    fields: parcelsFields,
    append: parcelsAppend,
    remove: parcelsRemove,
  } = useFieldArray({ control: form.control, name: "parcels" });

  // Auto-add pre-selected parcels from map
  useEffect(() => {
    if (preSelectedParcels && preSelectedParcels.length > 0 && parcelsFields.length === 0) {
      preSelectedParcels.forEach((p: any) => {
        // S11: kształt sub-obiektu (7point2/7point3) ma wymagane pola "3" i "4"
        // które wypełnia user dopiero przy edycji. Init z partial — cast na any
        // żeby ominąć strict shape check (Zod resolver i tak waliduje przy submit).
        parcelsAppend({
          "7point2": { "1": procedurePayload?.spatialPlanningActName || "", "2": p.id },
          "7point3": {},
          geometry: p.geometry,
        } as any);
      });
    }
  }, [preSelectedParcels]);

  type TFieldName = FieldPath<CreateApplicationFormValuesType>;
  const handlePostalCodeChange = (fieldName: TFieldName) => (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue(fieldName, formatPostalCode(e.target.value), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const addParcelFromSearch = (data: Parcel) => {
    parcelsAppend({
      "7point2": {
        "1": procedurePayload?.spatialPlanningActName || "",
        "2": data.id,
      },
      "7point3": {},
      geometry: data.geometry,
    } as any);
  };

  const addMultipleParcels = (parcels: Parcel[]) => {
    parcels.forEach((data) => {
      parcelsAppend({
        "7point2": {
          "1": procedurePayload?.spatialPlanningActName || "",
          "2": data.id,
        },
        "7point3": {},
        geometry: data.geometry,
      } as any);
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="sticky top-0 z-40 flex justify-end border-b bg-white/95 py-3 shadow-sm backdrop-blur">
          <Button
            type="submit"
            size="lg"
            className="h-14 w-full min-w-[250px] bg-primary px-8 text-base font-semibold text-primary-foreground shadow-lg hover:bg-primary/90 sm:w-auto"
          >
            <Send className="mr-2 h-5 w-5" />
            {isComment ? "Wyślij uwagę" : "Wyślij wniosek"}
          </Button>
        </div>

        {/* Draft recovery banner */}
        {draftRecovered && (
          <div className="bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-2 mb-4 flex items-center justify-between">
            <span className="text-sm text-yellow-800">Przywrócono zapisany szkic {letterGen}.</span>
            <button
              type="button"
              className="text-sm text-yellow-700 underline hover:text-yellow-900 ml-4"
              onClick={() => {
                clearDraft();
                form.reset(buildInitialValues(undefined, defaultValues));
              }}
            >
              Wyczyść
            </button>
          </div>
        )}

        {/* === SEKCJA 4: DANE WNIOSKODAWCY === */}
        <FormSection title="4. Dane składającego pismo">
          <p className="text-xs text-gray-500 mb-3">Wpisz swoje dane osobowe i adresowe. Wszystkie pola w tej sekcji są wymagane.</p>
          <FormFieldInput form={form} placeholder="np. Jan Kowalski" label="Imię i nazwisko lub nazwa" name="applicants.name" description="Wpisz pełne imię i nazwisko osoby składającej pismo lub nazwę podmiotu" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FormFieldInput form={form} placeholder="Polska" label="Kraj" name="applicants.country" description="Kraj zamieszkania" />
            <FormFieldSelect
              form={form}
              placeholder="Wybierz województwo"
              label="Województwo"
              name="applicants.voivodeship"
              options={voivodeships}
              onChange={fetchDistricts}
              description="Wybierz z listy"
            />
            <FormFieldSelect
              form={form}
              placeholder="Wybierz powiat"
              label="Powiat"
              name="applicants.district"
              options={districts}
              onChange={fetchTenants}
              description="Wybierz z listy"
            />
            <FormFieldSelect
              form={form}
              placeholder="Wybierz gminę"
              label="Gmina"
              name="applicants.tenant"
              options={tenants}
              description="Wybierz z listy"
            />
          </div>

          <div className="grid grid-cols-6 gap-3">
            <FormFieldInput form={form} placeholder="np. Kwiatowa" label="Ulica" name="applicants.street" className="col-span-3" description="Nazwa ulicy bez skrótu 'ul.'" />
            <FormFieldInput form={form} placeholder="np. 12" label="Nr domu" name="applicants.houseNumber" className="col-span-1" />
            <FormFieldInput form={form} placeholder="np. 5" label="Nr lokalu" name="applicants.apartmentNumber" className="col-span-1" description="Jeśli dotyczy" />
            <FormFieldInput form={form} placeholder="00-000" label="Kod pocztowy" name="applicants.postalCode" className="col-span-1" onChange={handlePostalCodeChange("applicants.postalCode")} description="Format: XX-XXX" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormFieldInput form={form} placeholder="np. Nowy Sącz" label="Miejscowość" name="applicants.town" description="Miejscowość zamieszkania" />
            <FormFieldInput form={form} placeholder="np. jan.kowalski@email.pl" label="E-mail" name="applicants.email" description={`Na ten adres wyślemy link potwierdzający i kopię ${letterGen}`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormFieldInput form={form} placeholder="np. +48 123 456 789" label="Nr telefonu" name="applicants.phone" description={`Nieobowiązkowe — ułatwia kontakt w sprawie ${letterGen}`} />
            <FormFieldInput form={form} placeholder="np. /nazwa/skrytka" label="Adres skrytki ePUAP" name="applicants.epuapAdress" description="Nieobowiązkowe — adres do doręczeń elektronicznych" />
          </div>

          {/* 4.1 Właściciel */}
          <div className="mt-3">
            <CheckboxGroup
              label="4.1. Czy składający pismo jest właścicielem lub użytkownikiem wieczystym nieruchomości objętej wnioskiem lub uwagą?"
              options={YES_NO_OPTIONS.map((type) => ({ label: type, id: type }))}
              isSingle
              value={form.watch("isOwnerOrPerpetualUsufructuary") ? [form.watch("isOwnerOrPerpetualUsufructuary")] : []}
              setValue={(val) => {
                const value = (Array.isArray(val) ? val[0] : val) as YesNoOptions;
                if (value) {
                  form.setValue("isOwnerOrPerpetualUsufructuary", value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                }
              }}
              error={form.formState.errors.isOwnerOrPerpetualUsufructuary?.message}
            />
          </div>
        </FormSection>

        {/* === SEKCJA 5: ADRES KORESPONDENCJI (checkbox) === */}
        <div className="border rounded-lg p-4 my-4">
          <div className="flex items-center gap-2 mb-3">
            <Checkbox
              id="showMailingAddress"
              checked={showMailingAddress}
              onCheckedChange={(checked) => {
                const enabled = !!checked;
                setShowMailingAddress(enabled);
                form.setValue(
                  "mailingAddress",
                  enabled ? (form.getValues("mailingAddress") || { ...EMPTY_MAILING_ADDRESS }) : null,
                  { shouldValidate: true, shouldDirty: true },
                );
              }}
            />
            <label htmlFor="showMailingAddress" className="font-semibold cursor-pointer">
              5. Adres do korespondencji (inny niż powyżej)
            </label>
            <span className="text-xs text-gray-400 ml-2">— zaznacz jeśli chcesz otrzymywać korespondencję na inny adres</span>
          </div>
          {showMailingAddress && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <FormFieldInput form={form} placeholder="Kraj" label="Kraj" name="mailingAddress.country" />
                <FormFieldInput form={form} placeholder="Województwo" label="Województwo" name="mailingAddress.voivodeship" />
                <FormFieldInput form={form} placeholder="Powiat" label="Powiat" name="mailingAddress.district" />
                <FormFieldInput form={form} placeholder="Gmina" label="Gmina" name="mailingAddress.tenant" />
              </div>
              <div className="grid grid-cols-6 gap-3">
                <FormFieldInput form={form} placeholder="Ulica" label="Ulica" name="mailingAddress.street" className="col-span-3" />
                <FormFieldInput form={form} placeholder="Nr domu" label="Nr domu" name="mailingAddress.houseNumber" className="col-span-1" />
                <FormFieldInput form={form} placeholder="Nr lokalu" label="Nr lokalu" name="mailingAddress.apartmentNumber" className="col-span-1" />
                <FormFieldInput form={form} placeholder="Kod pocztowy" label="Kod pocztowy" name="mailingAddress.postalCode" className="col-span-1" onChange={handlePostalCodeChange("mailingAddress.postalCode")} />
              </div>
              <FormFieldInput form={form} placeholder="Miejscowość" label="Miejscowość" name="mailingAddress.town" />
            </div>
          )}
        </div>

        {/* === SEKCJA 6: PEŁNOMOCNIK (checkbox) === */}
        <div className="border rounded-lg p-4 my-4">
          <div className="flex items-center gap-2 mb-3">
            <Checkbox
              id="showRepresentative"
              checked={showRepresentative}
              onCheckedChange={(checked) => {
                const enabled = !!checked;
                setShowRepresentative(enabled);
                form.setValue(
                  "representative",
                  enabled ? (form.getValues("representative") || { ...EMPTY_REPRESENTATIVE }) : null,
                  { shouldValidate: true, shouldDirty: true },
                );
              }}
            />
            <label htmlFor="showRepresentative" className="font-semibold cursor-pointer">
              6. Dane pełnomocnika
            </label>
            <span className="text-xs text-gray-400 ml-2">— zaznacz jeśli pismo składa pełnomocnik w Twoim imieniu</span>
          </div>
          {showRepresentative && (
            <div className="space-y-3">
              <CheckboxGroup
                options={STAKEHOLDER_TYPE.map((type) => ({ label: type, id: type }))}
                isSingle
                setValue={((val: string | string[]) => {
                  const value = (Array.isArray(val) ? val[0] : val) as StakholderType;
                  form.setValue("representative.type", value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                })}
                error={form.formState.errors?.representative?.message}
              />
              <FormFieldInput form={form} placeholder="Imię i nazwisko" label="Imię i nazwisko" name="representative.name" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <FormFieldInput form={form} placeholder="Kraj" label="Kraj" name="representative.country" />
                <FormFieldInput form={form} placeholder="Województwo" label="Województwo" name="representative.voivodeship" />
                <FormFieldInput form={form} placeholder="Powiat" label="Powiat" name="representative.district" />
                <FormFieldInput form={form} placeholder="Gmina" label="Gmina" name="representative.tenant" />
              </div>
              <div className="grid grid-cols-6 gap-3">
                <FormFieldInput form={form} placeholder="Ulica" label="Ulica" name="representative.street" className="col-span-3" />
                <FormFieldInput form={form} placeholder="Nr domu" label="Nr domu" name="representative.houseNumber" className="col-span-1" />
                <FormFieldInput form={form} placeholder="Nr lokalu" label="Nr lokalu" name="representative.apartmentNumber" className="col-span-1" />
                <FormFieldInput form={form} placeholder="Kod pocztowy" label="Kod pocztowy" name="representative.postalCode" className="col-span-1" onChange={handlePostalCodeChange("representative.postalCode")} />
              </div>
              <FormFieldInput form={form} placeholder="Miejscowość" label="Miejscowość" name="representative.town" />
              <div className="grid grid-cols-2 gap-3">
                <FormFieldInput form={form} placeholder="E-mail" label="E-mail" name="representative.email" />
                <FormFieldInput form={form} placeholder="Nr telefonu" label="Nr telefonu" name="representative.phone" />
              </div>
              <FormFieldInput form={form} placeholder="Adres ePUAP" label="Adres skrytki ePUAP" name="representative.epuapAdress" />
            </div>
          )}
        </div>

        {/* === SEKCJA 7: TREŚĆ PISMA I DZIAŁKI === */}
        <FormSection title="7. Treść pisma">
          {/* 7.1 Treść - zwinięta */}
          <FormSection title="7.1. Treść pisma" isCollapsible>
            <p className="text-xs text-gray-500 mb-2">
              {isComment
                ? "Opisz czego dotyczy Twoja uwaga do projektu planu. Możesz tu wpisać ogólną treść dotyczącą wszystkich wskazanych działek."
                : "Opisz czego dotyczy Twój wniosek lub uwaga. Możesz tu wpisać ogólną treść dotyczącą wszystkich wskazanych działek."}
              Jeśli dodajesz działki poniżej w sekcji 7.2, możesz wpisać treść osobno dla każdej działki w polu 7.2.4 — wtedy to pole nie jest wymagane.
            </p>
            <FormFieldTextarea
              form={form}
              placeholder={contentPlaceholder}
              label=""
              name="contentOfLetter"
            />
          </FormSection>

          {/* 7.2 DODAWANIE DZIAŁEK */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
            <h4 className="font-semibold mb-2">7.2. Dodaj działki ewidencyjne</h4>
            <p className="text-sm text-gray-600 mb-1">
              Wskaż działki ewidencyjne, których dotyczy pismo. Kliknij przycisk poniżej, aby otworzyć mapę i wybrać działki.
            </p>
            <p className="text-xs text-gray-400 mb-3">
              Dla każdej dodanej działki wpisz treść pisma (7.2.4). Sekcja 7.3 (symbol przeznaczenia i parametry zabudowy) jest
              nieobowiązkowa.
            </p>
            <div className="flex gap-2 flex-wrap">
              <SearchLocation
                addParcels={addMultipleParcels}
                initialCenter={procedureMapCenter}
                initialZoom={procedureMapZoom}
              />
              {applicantsParcels?.map((parcel) => (
                <Button
                  key={parcel.id}
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    addParcelFromSearch(parcel);
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" /> {parcel.id}
                </Button>
              ))}
            </div>
          </div>

          {/* LISTA DZIAŁEK Z FORMULARZAMI */}
          {parcelsFields.length > 0 && (
            <div className="space-y-4">
              {parcelsFields.map((field, index) => (
                <div key={field.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold">Działka {index + 1}</h4>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={(e) => { e.preventDefault(); parcelsRemove(index); }}>
                      <Trash2 className="h-4 w-4 mr-1" /> Usuń
                    </Button>
                  </div>

                  {/* 7.2 */}
                  <div className="space-y-3 mb-4">
                    <h5 className="text-sm font-medium text-gray-500">Sekcja 7.2 — Informacje o działce</h5>
                    <div className="grid grid-cols-2 gap-3">
                      <FormFieldInput
                        form={form}
                        placeholder="Automatycznie z procedury"
                        label="7.2.1. Nazwa aktu planowania przestrzennego"
                        name={`parcels.${index}.7point2.1` as any}
                        disabled
                        description="Uzupełniane automatycznie na podstawie wybranej procedury"
                      />
                      <FormFieldInput
                        form={form}
                        placeholder="np. 120601_1.0001.289"
                        label="7.2.2. Identyfikator działki ewidencyjnej"
                        name={`parcels.${index}.7point2.2` as any}
                        description="Numer TERYT działki — uzupełniany automatycznie po wybraniu na mapie"
                      />
                    </div>
                    <CheckboxGroup
                      label="7.2.3. Czy teren objęty pismem obejmuje całość działki lub działek ewidencyjnych?"
                      options={YES_NO_OPTIONS.map((type) => ({ label: type, id: type }))}
                      isSingle
                      setValue={((val: string | string[]) => {
                        const value = (Array.isArray(val) ? val[0] : val) as YesNoOptions;
                        form.setValue(`parcels.${index}.7point2.3` as any, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                      })}
                      error={form.formState.errors?.parcels?.[index]?.["7point2"]?.[3]?.message}
                    />
                    <FormFieldTextarea
                      form={form}
                      placeholder={contentPlaceholder}
                      label="7.2.4. Treść *"
                      name={`parcels.${index}.7point2.4` as any}
                      description={PARCEL_CONTENT_HELP}
                      tooltip={PARCEL_CONTENT_HELP}
                    />
                  </div>

                  {/* 7.3 */}
                  <div className="space-y-3 border-t pt-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h5 className="text-sm font-medium text-gray-500">
                        Sekcja 7.3 (nieobowiązkowa) — {isComment ? "Proponowane przeznaczenie i parametry zagospodarowania" : "Wnioskowane parametry zagospodarowania"}
                      </h5>
                      <button
                        type="button"
                        onClick={() =>
                          form.setValue(`parcels.${index}.7point3` as any, {} as any, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        className="text-xs text-gray-500 underline hover:text-gray-800">
                        Wyczyść sekcję 7.3
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">
                      {isMPZP
                        ? "Możesz ją pominąć. Jeśli ją wypełniasz, wybierz symbol przeznaczenia z legendy projektu planu (7.3.4); parametry 7.3.5–7.3.7 są opcjonalne."
                        : "Możesz ją pominąć. Wypełnij w przypadku wniosku do planu ogólnego (3.1) lub MPZP (3.2) — wtedy wskaż symbol przeznaczenia (7.3.4); parametry 7.3.5–7.3.7 są opcjonalne."}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {symbolOptions ? (
                        <FormFieldSelect
                          form={form}
                          placeholder="Wybierz symbol"
                          label="7.3.4. Symbol przeznaczenia"
                          name={`parcels.${index}.7point3.4` as any}
                          options={symbolOptions}
                          description={DESTINATION_SYMBOL_HELP}
                          tooltip={DESTINATION_SYMBOL_HELP}
                        />
                      ) : (
                        <FormFieldInput
                          form={form}
                          placeholder="np. MN, SJ, P..."
                          label="7.3.4. Symbol przeznaczenia"
                          name={`parcels.${index}.7point3.4` as any}
                          description={DESTINATION_SYMBOL_HELP}
                          tooltip={DESTINATION_SYMBOL_HELP}
                        />
                      )}
                      <FormFieldInput
                        form={form}
                        placeholder="np. 50"
                        label="7.3.5. Maks. zabudowa [%]"
                        name={`parcels.${index}.7point3.5` as any}
                        description="Opcjonalne — wnioskowany maks. udział powierzchni zabudowy"
                      />
                      <FormFieldInput
                        form={form}
                        placeholder="np. 10"
                        label="7.3.6. Maks. wysokość [m]"
                        name={`parcels.${index}.7point3.6` as any}
                        description="Opcjonalne — wnioskowana maks. wysokość zabudowy"
                      />
                      <FormFieldInput
                        form={form}
                        placeholder="np. 30"
                        label="7.3.7. Min. pow. biol. [%]"
                        name={`parcels.${index}.7point3.7` as any}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </FormSection>

        <FormSection title="8. Oświadczenie w sprawie korespondencji elektronicznej">
          <CheckboxGroup
            label="Czy wyrażasz zgodę na doręczanie korespondencji w tej sprawie za pomocą środków komunikacji elektronicznej?"
            options={[
              { label: "Wyrażam zgodę", id: "Tak" },
              { label: "Nie wyrażam zgody", id: "Nie" },
            ]}
            isSingle
            value={form.watch("electronicCorrespondenceConsent") ? [form.watch("electronicCorrespondenceConsent")] : []}
            setValue={(val) => {
              const value = (Array.isArray(val) ? val[0] : val) as YesNoOptions;
              if (value) {
                form.setValue("electronicCorrespondenceConsent", value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
              }
            }}
            error={form.formState.errors.electronicCorrespondenceConsent?.message}
          />
        </FormSection>

        {/* ZAŁĄCZNIKI - NA KOŃCU */}
        <FormSection title="9. Załączniki">
          <p className="text-xs text-gray-500 mb-2">
            Dołącz dodatkowe dokumenty, np. pełnomocnictwo, mapę z określeniem granic terenu, lub inne załączniki.
            Maksymalny rozmiar pliku: 1 GB. Akceptowane formaty: PDF, DOC, DOCX, JPG, PNG, GeoTIFF, GML, SHP, ZIP.
          </p>
          <CheckboxGroup
            label="Zaznacz rodzaje dołączanych załączników"
            options={[
              { id: "powerOfAttorney", label: "Pełnomocnictwo do reprezentowania składającego pismo lub pełnomocnictwo do doręczeń" },
              { id: "stampDutyProof", label: "Potwierdzenie uiszczenia opłaty skarbowej od pełnomocnictwa" },
              { id: "graphicBoundary", label: "Określenie granic terenu w formie graficznej" },
              { id: "other", label: "Inne załączniki" },
            ]}
            column
            value={form.watch("attachmentTypes") || []}
            setValue={(val) => {
              const values = (Array.isArray(val) ? val : [val]).filter(Boolean) as AttachmentType[];
              form.setValue("attachmentTypes", values, { shouldValidate: true, shouldDirty: true });
            }}
          />
          <FormFieldInput multiple form={form} inputType="file" placeholder="Wybierz pliki" label="Dołącz pliki" name="files" />
        </FormSection>
      </form>
    </Form>
  );
}

export default CreateApplicationForm;
